import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';

export const Portfolio: React.FC = () => {
  const projects = [
    {
      title: "InnovateTech Solutions",
      category: "Tech Startup",
      slug: "tech-startup",
      image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "A comprehensive B2B SaaS platform with custom dashboards, real-time analytics, and seamless payment integration.",
      technologies: ["React", "Node.js", "MongoDB", "Stripe API"],
      results: "300% increase in user engagement, 45% improvement in lead generation"
    },
    {
      title: "Wellness Family Medical",
      category: "Medical Clinic",
      slug: "medical-clinic",
      image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "HIPAA-compliant medical practice website with patient portal, online appointment booking, and telemedicine integration.",
      technologies: ["WordPress", "PHP", "MySQL", "HIPAA Compliance"],
      results: "85% of appointments now booked online, 96% patient satisfaction"
    },
    {
      title: "QuickCare Urgent Centers",
      category: "Urgent Care",
      slug: "urgent-care",
      image: "https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Multi-location urgent care platform with real-time wait times, online check-in, and location-based services.",
      technologies: ["WordPress", "PHP", "MySQL", "Google Maps API"],
      results: "35% reduction in wait times, 70% of patients use online check-in"
    },
    {
      title: "Bella Vista Italian Bistro",
      category: "Restaurant",
      slug: "restaurant",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Complete restaurant platform with online ordering, delivery integration, table reservations, and loyalty program.",
      technologies: ["WordPress", "WooCommerce", "Stripe API", "AJAX"],
      results: "250% increase in online orders, 40% overall revenue growth"
    },
    {
      title: "TrendMart Global",
      category: "Dropshipping Business",
      slug: "dropshipping",
      image: "https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Fully automated dropshipping platform managing 10,000+ products with supplier integration and order automation.",
      technologies: ["WooCommerce", "PHP", "MySQL", "API Integration"],
      results: "$150K monthly revenue, 95% automated order fulfillment"
    },
    {
      title: "Radiance Medical Spa",
      category: "Medical Spa",
      slug: "medical-spa",
      image: "https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Sophisticated medical spa platform with advanced booking system, treatment packages, and membership programs.",
      technologies: ["React", "Node.js", "MySQL", "Stripe API"],
      results: "80% online bookings, 60% revenue increase, 75% client retention improvement"
    },
    {
      title: "Sterling & Associates Law",
      category: "Law Firm",
      slug: "law-firm",
      image: "https://images.pexels.com/photos/5668772/pexels-photo-5668772.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Professional legal practice platform with secure client portal, case management, and consultation booking.",
      technologies: ["WordPress", "PHP", "MySQL", "SSL Security"],
      results: "180% increase in qualified leads, 92% client satisfaction"
    },
    {
      title: "Premier Auto Group",
      category: "Auto Dealer",
      slug: "auto-dealer",
      image: "https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "Comprehensive automotive dealership platform with inventory management, financing tools, and service scheduling.",
      technologies: ["React", "Node.js", "MySQL", "Third-party APIs"],
      results: "220% increase in online leads, 85% improvement in inventory views"
    },
    {
      title: "Precision Components Inc.",
      category: "Manufacturing",
      slug: "manufacturing",
      image: "https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      description: "B2B manufacturing platform with advanced product catalog, customer portal, and integrated order management.",
      technologies: ["React", "Java", "MySQL", "REST APIs"],
      results: "150% increase in B2B leads, 60% faster order processing"
    }
  ];

  const testimonials = [
    {
      client: "Sarah Johnson, InnovateTech Solutions",
      quote: "OutstandingNet delivered beyond our expectations. The new platform not only looks amazing but has significantly improved our business metrics."
    },
    {
      client: "Dr. Emily Rodriguez, Wellness Family Medical",
      quote: "The patient portal has revolutionized our practice. Our patients love the convenience, and our staff has more time to focus on patient care."
    },
    {
      client: "Maria Rossi, Bella Vista Italian Bistro",
      quote: "OutstandingNet transformed our business during a critical time. The online ordering system helped us thrive, increasing our revenue by 40%."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-32 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Our Portfolio
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Explore our latest projects and see how we've helped businesses transform their online presence across various industries.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {projects.map((project, index) => (
              <div key={index} className="card overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Link 
                      to={`/portfolio/${project.slug}`}
                      className="bg-white text-gray-900 px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                    >
                      <span>View Case Study</span>
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-primary-100 text-primary-600 text-sm font-medium rounded-full">
                      {project.category}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{project.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIndex) => (
                        <span key={techIndex} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Results:</h4>
                    <p className="text-accent-600 font-medium">{project.results}</p>
                  </div>

                  <Link 
                    to={`/portfolio/${project.slug}`}
                    className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                  >
                    Read Full Case Study
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's feedback from some of our satisfied clients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card p-8">
                <p className="text-gray-600 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div className="font-semibold text-gray-900">
                  {testimonial.client}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">500+</div>
              <div className="text-xl text-primary-100">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">98%</div>
              <div className="text-xl text-primary-100">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">50+</div>
              <div className="text-xl text-primary-100">Industries Served</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">5+</div>
              <div className="text-xl text-primary-100">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Let's discuss your vision and create something outstanding together. 
            Get a free consultation and see how we can help transform your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center">
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link to="/services" className="btn-outline text-lg px-8 py-4">
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};